import { useEffect, useState } from "react";
import { functions, firestore } from "../firebase";
import { httpsCallable } from "firebase/functions";
import {
  collection,
  query,
  orderBy,
  limit,
  onSnapshot,
  DocumentData,
} from "firebase/firestore";
import styled from "@emotion/styled";
import ReactDiffViewer, { DiffMethod } from "react-diff-viewer";

// Styled components for the UI
const Container = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  min-height: 100vh;
  background: #1e1e1e;
  color: #f0f0f0;
  display: flex;
  flex-direction: column;
  font-family: "Roboto Mono", monospace;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 15px;
  background: #2d2d2d;
  border-bottom: 1px solid #3d3d3d;
`;

const Button = styled.button`
  background: #4a4a4a;
  color: #fff;
  border: none;
  border-radius: 4px;
  padding: 6px 12px;
  cursor: pointer;
  margin-left: 10px;
  font-size: 12px;

  &:hover {
    background: #5a5a5a;
  }
`;

const Content = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0;
`;

const DocumentGroup = styled.div`
  margin-bottom: 10px;
  border-bottom: 1px solid #3d3d3d;
`;

const DocumentHeader = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 8px 15px;
  background: #2a2a2a;
  cursor: pointer;
  align-items: center;

  &:hover {
    background: #333;
  }
`;

const DocumentContent = styled.pre<{ isExpanded: boolean }>`
  padding: 10px 15px;
  margin: 0;
  background: #111;
  font-size: 12px;
  white-space: pre-wrap;
  overflow-x: auto;
  display: ${({ isExpanded }) => (isExpanded ? "block" : "none")};
`;

const Badge = styled.span<{ type: string }>`
  display: inline-block;
  padding: 2px 6px;
  border-radius: 3px;
  font-size: 14px;
  margin-right: 8px;
  font-weight: bold;
  background: ${({ type }) => {
    switch (type) {
      case "create":
        return "#4caf50";
      case "update":
        return "#2196f3";
      case "delete":
        return "#f44336";
      default:
        return "#9e9e9e";
    }
  }};
`;

const Timestamp = styled.span`
  font-size: 11px;
  color: #aaa;
`;

const StatsBar = styled.div`
  padding: 6px 15px;
  background: #1a1a1a;
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  border-bottom: 1px solid #3d3d3d;
`;

const FilterControls = styled.div`
  padding: 6px 15px;
  background: #1a1a1a;
  display: flex;
  align-items: center;
  font-size: 12px;
  border-bottom: 1px solid #3d3d3d;
`;

const SearchInput = styled.input`
  background: #333;
  color: white;
  border: 1px solid #555;
  padding: 5px 10px;
  border-radius: 4px;
  margin-left: 10px;
  width: 300px;
`;

// Types for the write operations
interface WriteOperation {
  id: string;
  path: string;
  documentId: string;
  collection: string;
  timestamp: number;
  data: DocumentData | null;
  previousData: DocumentData | null;
  operation: "create" | "update" | "delete";
}

export const DevFirestoreLogger: React.FC = () => {
  const [writes, setWrites] = useState<WriteOperation[]>([]);
  const [expandedDocs, setExpandedDocs] = useState<Record<string, boolean>>({});
  const [filterText, setFilterText] = useState<string>("");
  const [expandAll, setExpandAll] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    // Subscribe to real-time updates from the Firestore _devLogs collection
    const q = query(
      collection(firestore, "_devLogs"),
      orderBy("timestamp", "desc"),
      limit(200),
    );

    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const updatedWrites: WriteOperation[] = [];
      querySnapshot.forEach((doc) => {
        updatedWrites.push(doc.data() as WriteOperation);
      });
      setWrites(updatedWrites);
    });

    return () => unsubscribe();
  }, []);

  const toggleExpand = (id: string) => {
    setExpandedDocs((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const toggleExpandAll = () => {
    if (expandAll) {
      setExpandedDocs({});
    } else {
      const allExpanded: Record<string, boolean> = {};
      Object.keys(docGroups).forEach((path) => {
        allExpanded[path] = true;
      });
      setExpandedDocs(allExpanded);
    }
    setExpandAll(!expandAll);
  };

  const clearLogs = async () => {
    setLoading(true);
    const clearDevLogs = httpsCallable(functions, "clearDevLogs");
    await clearDevLogs();
    setWrites([]);
    setLoading(false);
  };

  // Filter operations by text in path
  const filteredWrites = filterText
    ? writes.filter((w) =>
        w.path.toLowerCase().includes(filterText.toLowerCase()),
      )
    : writes;

  // Group documents by path
  const docGroups: Record<string, WriteOperation[]> = {};
  filteredWrites.forEach((write) => {
    if (!docGroups[write.path]) {
      docGroups[write.path] = [];
    }
    docGroups[write.path].push(write);
  });

  // Format date
  const formatDate = (timestamp: number) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString();
  };

  // Count operations by type
  const createCount = writes.filter((w) => w.operation === "create").length;
  const updateCount = writes.filter((w) => w.operation === "update").length;
  const deleteCount = writes.filter((w) => w.operation === "delete").length;

  return (
    <Container>
      <Header>
        <h3>Firestore Writes Monitor</h3>
        <div>
          <Button onClick={clearLogs} disabled={loading}>
            {loading ? "Clearing..." : "Clear All"}
          </Button>
        </div>
      </Header>

      <StatsBar>
        <div>
          <Badge type="create">{createCount} creates</Badge>
          <Badge type="update">{updateCount} updates</Badge>
          <Badge type="delete">{deleteCount} deletes</Badge>
          <span style={{ marginLeft: "10px" }}>
            Total: {writes.length} operations
          </span>
        </div>
        <Button onClick={toggleExpandAll}>
          {expandAll ? "Collapse All" : "Expand All"}
        </Button>
      </StatsBar>

      <FilterControls>
        <span>Filter by path:</span>
        <SearchInput
          type="text"
          value={filterText}
          onChange={(e) => setFilterText(e.target.value)}
          placeholder="Enter document path to filter..."
        />
      </FilterControls>

      <Content>
        {Object.entries(docGroups).map(([path, operations]) => (
          <DocumentGroup key={path}>
            <DocumentHeader>
              <div
                style={{
                  wordWrap: "break-word",
                  maxWidth: "calc(100% - 90px)",
                  display: "inline-block",
                }}
              >
                <strong>{path}</strong>
                <span
                  style={{
                    marginLeft: "10px",
                    fontSize: "12px",
                    color: "#aaa",
                    wordWrap: "normal",
                    whiteSpace: "nowrap",
                  }}
                >
                  ({operations.length} operations)
                </span>
              </div>
              <div>
                <Button onClick={() => toggleExpand(path)}>
                  {expandedDocs[path] ? "Collapse" : "Expand"}
                </Button>
              </div>
            </DocumentHeader>

            <DocumentContent
              isExpanded={expandedDocs[path] || false}
              key={path + "content"}
            >
              {operations.map((op, index) => (
                <div
                  key={op.id + "content" + index}
                  style={{
                    marginBottom: "15px",
                    backgroundColor: index % 2 === 1 ? "#222" : "transparent",
                    padding: "8px",
                    borderRadius: "4px",
                  }}
                >
                  <div>
                    <Badge type={op.operation}>{op.operation}</Badge>
                    <Timestamp>{formatDate(op.timestamp)}</Timestamp>
                  </div>
                  <ReactDiffViewer
                    oldValue={JSON.stringify(op.previousData, null, 2)}
                    newValue={JSON.stringify(op.data, null, 2)}
                    splitView={true}
                    disableWordDiff={false}
                    leftTitle="Previous"
                    rightTitle="Current"
                    useDarkTheme={true}
                    compareMethod={DiffMethod.WORDS}
                    styles={{
                      variables: {
                        dark: {
                          diffViewerBackground: "#1e1e1e",
                          diffViewerColor: "#f0f0f0",
                          addedBackground: "rgba(71, 175, 74, 0.2)",
                          addedColor: "#f0f0f0",
                          removedBackground: "rgba(229, 83, 75, 0.2)",
                          removedColor: "#f0f0f0",
                          wordAddedBackground: "rgba(71, 175, 74, 0.4)",
                          wordRemovedBackground: "rgba(229, 83, 75, 0.4)",
                          addedGutterBackground: "rgba(71, 175, 74, 0.4)",
                          removedGutterBackground: "rgba(229, 83, 75, 0.4)",
                          gutterBackground: "#1e1e1e",
                          gutterColor: "#f0f0f0",
                          codeFoldBackground: "#222",
                        },
                      },
                    }}
                  />
                </div>
              ))}
            </DocumentContent>
          </DocumentGroup>
        ))}
      </Content>
    </Container>
  );
};
